import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import { API_ENDPOINT, APP_ID } from "../../utils/constants";
import { getDbHeader } from "../../utils/util";

const CategorySelectorAccordions = ({ title = "", onSelectionChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const abortController = useMemo(() => new AbortController(), []);

  // Fetch categories with cleanup
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${API_ENDPOINT}event/get-product-categories`,
          {
            ...getDbHeader(APP_ID),
            signal: abortController.signal,
          }
        );
        console.log("API response:", response.data);
        setData(response.data);
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error(err);
          setError("Failed to load categories");
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => abortController.abort();
  }, [abortController]);

  // Optimized category check with Map
  const isCategorySelected = useCallback(
    (category) => {
      return selectedCategories.some(
        (sel) =>
          sel.MainCategory === category.MainCategory &&
          (sel.Category ?? null) === (category.Category ?? null) &&
          (sel.SubCategory ?? null) === (category.SubCategory ?? null)
      );
    },
    [selectedCategories]
  );

  const handleCheckboxChange = useCallback(
    (category) => {
    
      setSelectedCategories((prev) => {
        const exists = prev.some(
          (sel) =>
            sel.MainCategory === category.MainCategory &&
            (sel.Category ?? null) === (category.Category ?? null) &&
            (sel.SubCategory ?? null) === (category.SubCategory ?? null)
        );

        const newSelections = exists
          ? prev.filter(
              (sel) =>
                !(
                  sel.MainCategory === category.MainCategory &&
                  (sel.Category ?? null) === (category.Category ?? null) &&
                  (sel.SubCategory ?? null) === (category.SubCategory ?? null)
                )
            )
          : [...prev, category];
        onSelectionChange?.(newSelections);
        return newSelections;
      });
    },
    [onSelectionChange]
  );

  // Memoized grouped data
  const { mainGroups, categoryGroups } = useMemo(() => {
    if (!data?.categories) return { mainGroups: {}, categoryGroups: {} };

    const groups = data.categories.reduce((acc, cat) => {
      const main = cat.MainCategory || "Unknown";
      if (!acc[main]) acc[main] = [];
      acc[main].push(cat);
      return acc;
    }, {});

    const catGroups =
      data.type === "triple"
        ? data.categories.reduce((acc, item) => {
            const key = `${item.MainCategory}-${item.Category}`;
            if (!acc[key]) acc[key] = [];
            acc[key].push(item);
            return acc;
          }, {})
        : {};

  
    return { mainGroups: groups, categoryGroups: catGroups };
  }, [data]);

  // Render helpers
  const renderSingle = (mainCategory) => {
    const categoryObj = {
      MainCategory: mainCategory,
      Category: null,
      SubCategory: null,
    };

    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={isCategorySelected(categoryObj)}
            onChange={() => handleCheckboxChange(categoryObj)}
          />
        }
        label={<Typography>{mainCategory}</Typography>}
      />
    );
  };

  const renderDouble = (items) =>
    items.map((cat, idx) => (
      <FormControlLabel
        sx={{ width: "100%" }}
        key={`${cat.MainCategory}-${cat.Category}-${idx}`}
        control={
          <Checkbox
            checked={isCategorySelected(cat)}
            onChange={() => handleCheckboxChange(cat)}
          />
        }
        label={<Typography variant="body2">{cat.Category || "N/A"}</Typography>}
      />
    ));

  const renderTriple = (mainCategory) =>
    Object.entries(categoryGroups)
      .filter(([key]) => key.startsWith(mainCategory))
      .map(([key, items]) => (
        <Box key={key} mb={2}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            {key.split("-")[1]}
          </Typography>
          {items.map((subItem, subIdx) => (
            <FormControlLabel
              sx={{ width: "100%" }}
              key={`${key}-${subIdx}`}
              control={
                <Checkbox
                  checked={isCategorySelected(subItem)}
                  onChange={() => handleCheckboxChange(subItem)}
                />
              }
              label={
                <Typography variant="body2">
                  {subItem.SubCategory || "N/A"}
                </Typography>
              }
            />
          ))}
        </Box>
      ));

  if (loading)
    return (
      <Box textAlign="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data?.categories?.length)
    return <Alert severity="info">No categories available</Alert>;

  return (
    <Box my={2}>
      {title && (
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {title}
        </Typography>
      )}

      {Object.entries(mainGroups).map(([mainCategory, items]) => (
        <Accordion key={`${mainCategory}-${data.type}`}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">
              {mainCategory}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {data.type === "single" && renderSingle(mainCategory)}
            {data.type === "double" && renderDouble(items)}
            {data.type === "triple" && renderTriple(mainCategory)}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default React.memo(CategorySelectorAccordions);